import React, { useRef, useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Alert,
  Collapse,
  Stack,
  CircularProgress,
  Autocomplete
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon, Close as CloseIcon } from '@mui/icons-material';
import useProductImporter from '../hooks/useProductImporter';
import { useProducts } from '../contexts/ProductsContext';
import type { Product } from '../types/product';
import { storage, db, auth } from '../firebase/config';
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { PRODUCT_CATEGORIES, getSubcategories, getAudiencesForCategory } from '../constants/categories';
import { collection, getDocs, query, where, updateDoc, serverTimestamp, doc } from 'firebase/firestore';
import { formatGBP } from '../utils/currency';

const AdminPanel: React.FC = () => {
  const { products, addProduct, updateProduct, deleteProduct } = useProducts();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const { fetchProductFromUrl, isLoading: isImporting, error: importError, resetError } = useProductImporter();
  const [importUrl, setImportUrl] = useState('');
  const [showImportForm, setShowImportForm] = useState(false);
  const [uploadedImages, setUploadedImages] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [formData, setFormData] = useState<Omit<Product, 'id' | 'createdAt' | 'updatedAt'>>({
    name: '',
    description: '',
    brand: '',
    subcategory: '',
    audience: '' as any,
    price: 0,
    originalPrice: 0,
    isOnSale: false,
    image: '',
    images: [],
    url: '',
    category: 'Other' as const,
    tags: [],
    stock: 0,
    isInStock: true,
    isFeatured: false,
    isNew: false,
    rating: 0,
    reviewCount: 0,
  });

  const handleOpenDialog = (product: Product | null = null) => {
    if (product) {
      setEditingProduct(product);
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { id, createdAt, updatedAt, ...rest } = product;
      // Ensure url is a string for controlled TextField
      setFormData({
        ...rest,
        brand: (rest as any).brand ?? '',
        subcategory: (rest as any).subcategory ?? '',
        audience: (rest as any).audience ?? '',
        url: (rest as any).url ?? '',
      });
      setUploadedImages(Array.isArray(rest.images) ? rest.images.filter((u) => !!u) : []);
    } else {
      setEditingProduct(null);
      setFormData({
        name: '',
        description: '',
        brand: '',
        subcategory: '',
        audience: '' as any,
        price: 0,
        originalPrice: 0,
        isOnSale: false,
        image: '',
        images: [],
        url: '',
        category: 'Other' as const,
        tags: [],
        stock: 0,
        isInStock: true,
        isFeatured: false,
        isNew: false,
        rating: 0,
        reviewCount: 0,
      });
      setUploadedImages([]);
    }
    setIsDialogOpen(true);
  };

  const handleRemoveImage = async (url: string) => {
    try {
      // Try to delete from storage (best-effort)
      const objRef = storageRef(storage, url);
      await deleteObject(objRef);
    } catch (err) {
      console.warn('Failed to delete image from storage, removing from list only:', err);
    } finally {
      setUploadedImages(prev => prev.filter(u => u !== url));
      setFormData(prev => ({ ...prev, image: prev.image === url ? '' : prev.image }));
    }
  };

  const handleDeleteClick = (e: React.MouseEvent, id: number) => {
    e.stopPropagation();
    deleteProduct(id);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFilesSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploadError(null);
    const uid = auth.currentUser?.uid;
    if (!uid) {
      setUploadError('You must be logged in to upload images.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    setIsUploading(true);
    try {
      const uploadedUrls: string[] = [];
      // Uploads must go under products/uploads/{uid}/{filename}
      const basePath = `products/uploads/${uid}`;
      for (const file of Array.from(files)) {
        // Client-side validation to mirror Storage rules
        if (!file.type.startsWith('image/')) {
          setUploadError('Only image files are allowed.');
          continue;
        }
        if (file.size > 5 * 1024 * 1024) {
          setUploadError('File too large. Max size is 5 MB.');
          continue;
        }
        const safeName = `${Date.now()}-${file.name.replace(/\s+/g, '-')}`;
        const objRef = storageRef(storage, `${basePath}/${safeName}`);
        await uploadBytes(objRef, file, { contentType: file.type });
        const url = await getDownloadURL(objRef);
        uploadedUrls.push(url);
      }
      if (uploadedUrls.length === 0) {
        setIsUploading(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
        return;
      }
      // If no main image set, set it to the first uploaded
      setFormData(prev => ({ ...prev, image: prev.image || uploadedUrls[0] || '' }));
      // Append uploaded URLs into uploadedImages (unique)
      setUploadedImages(prev => Array.from(new Set([...(prev || []), ...uploadedUrls])));
    } catch (err) {
      console.error('Upload failed', err);
      const msg = err instanceof Error ? err.message : 'Upload failed';
      setUploadError(msg);
    } finally {
      setIsUploading(false);
      // reset input so selecting the same files again triggers onChange
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Normalize URL: trim and ensure scheme
    const trimmedUrl = (formData.url || '').trim();
    const normalizedUrl = trimmedUrl
      ? (/^https?:\/\//i.test(trimmedUrl) ? trimmedUrl : `https://${trimmedUrl}`)
      : '';
    // Build final images list from main image + uploaded images
    const finalImages = Array.from(new Set([
      formData.image,
      ...uploadedImages,
    ].filter(Boolean)));

    // Ensure numeric fields are numbers in payload
    const payload = {
      ...formData,
      // normalize optional text fields
      brand: (formData as any).brand && String((formData as any).brand).trim() !== '' ? String((formData as any).brand).trim() : undefined,
      subcategory: (formData as any).subcategory && String((formData as any).subcategory).trim() !== '' ? String((formData as any).subcategory).trim() : undefined,
      audience: (formData as any).audience && String((formData as any).audience).trim() !== '' ? String((formData as any).audience).trim() : undefined,
      price: Number(formData.price ?? 0),
      originalPrice: formData.originalPrice !== undefined ? Number(formData.originalPrice) : undefined,
      rating: formData.rating !== undefined ? Number(formData.rating) : undefined,
      reviewCount: formData.reviewCount !== undefined ? Number(formData.reviewCount) : undefined,
      stock: Number(formData.stock ?? 0),
      url: normalizedUrl || undefined,
      images: finalImages,
    } as typeof formData & { images: string[] };

    if (editingProduct) {
      await updateProduct(editingProduct.id, {
        ...payload,
        updatedAt: new Date(),
      });
    } else {
      await addProduct(payload);
    }
    handleCloseDialog();
  };

  const handleImportProduct = async () => {
    if (!importUrl) return;
    
    const productData = await fetchProductFromUrl(importUrl);
    if (productData) {
      setFormData(prev => ({
        ...prev,
        ...productData
      }));
      setShowImportForm(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    const numericFields = new Set(['price', 'originalPrice', 'rating', 'reviewCount', 'stock']);
    let newValue: unknown = value;
    if (type === 'checkbox') {
      newValue = checked;
    } else if (type === 'number' || numericFields.has(name)) {
      newValue = value === '' ? 0 : Number(value);
    }
    setFormData(prev => ({
      ...prev,
      [name]: newValue as any,
    }));
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* User Management: Promote/Demote Admin */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h5" sx={{ mb: 2 }}>User Management</Typography>
        <UserRoleManager />
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">Product Management</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Product
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Image</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Subcategory</TableCell>
              <TableCell>Price</TableCell>
              <TableCell>Stock</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {products.map((product) => (
              <TableRow key={product.id}>
                <TableCell>
                  <img 
                    src={product.image} 
                    alt={product.name} 
                    style={{ width: 50, height: 50, objectFit: 'cover' }} 
                  />
                </TableCell>
                <TableCell>{product.name}</TableCell>
                <TableCell>{product.category}</TableCell>
                <TableCell>{product.subcategory || '-'}</TableCell>
                <TableCell>{formatGBP(Number(product.price ?? 0))}</TableCell>
                <TableCell>{product.stock}</TableCell>
                <TableCell>
                  {product.isInStock ? (
                    <Typography color="success.main">In Stock</Typography>
                  ) : (
                    <Typography color="error">Out of Stock</Typography>
                  )}
                </TableCell>
                <TableCell>
                  <IconButton onClick={() => handleOpenDialog(product)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton 
                    color="error" 
                    onClick={(e) => handleDeleteClick(e, product.id)}
                    aria-label="delete"
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={isDialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <span>{editingProduct ? 'Edit Product' : 'Add New Product'}</span>
            {!editingProduct && !showImportForm && (
              <Button 
                variant="outlined" 
                size="small"
                onClick={() => setShowImportForm(true)}
                disabled={isImporting}
              >
                Import from URL
              </Button>
            )}
          </Box>
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Collapse in={showImportForm}>
              <Alert 
                severity="info" 
                sx={{ mb: 2 }}
                action={
                  <IconButton
                    aria-label="close"
                    color="inherit"
                    size="small"
                    onClick={() => {
                      setShowImportForm(false);
                      resetError();
                    }}
                  >
                    <CloseIcon fontSize="inherit" />
                  </IconButton>
                }
              >
                <Typography variant="subtitle2" gutterBottom>
                  Paste product URL to import details
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="https://example.com/product/123"
                    value={importUrl}
                    onChange={(e) => setImportUrl(e.target.value)}
                    disabled={isImporting}
                  />
                  <Button 
                    variant="contained" 
                    onClick={handleImportProduct}
                    disabled={!importUrl || isImporting}
                    startIcon={isImporting ? <CircularProgress size={20} /> : null}
                  >
                    {isImporting ? 'Importing...' : 'Import'}
                  </Button>
                </Stack>
                {importError && (
                  <Typography color="error" variant="caption" display="block" sx={{ mt: 1 }}>
                    {importError}
                  </Typography>
                )}
              </Alert>
            </Collapse>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
              <Box>
                <TextField
                  fullWidth
                  label="Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  margin="normal"
                  required
                />
                <TextField
                  fullWidth
                  label="Brand (optional)"
                  name="brand"
                  value={(formData as any).brand || ''}
                  onChange={handleChange}
                  margin="normal"
                />
                <TextField
                  fullWidth
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  margin="normal"
                  multiline
                  rows={4}
                  required
                />
                <Autocomplete
                  options={PRODUCT_CATEGORIES as unknown as string[]}
                  freeSolo
                  value={formData.category || ''}
                  onChange={(_, newValue) => {
                    setFormData(prev => ({ ...prev, category: (newValue || '') as any, subcategory: '', audience: '' as any }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      label="Category"
                      margin="normal"
                      onChange={(e) => {
                        const v = e.target.value;
                        setFormData(prev => ({ ...prev, category: v as any, subcategory: '', audience: '' as any }));
                      }}
                      required
                    />
                  )}
                />
                <Autocomplete
                  options={getSubcategories(formData.category) as unknown as string[]}
                  freeSolo
                  value={(formData as any).subcategory || ''}
                  onChange={(_, newValue) => {
                    setFormData(prev => ({ ...prev, subcategory: (newValue || '') as any }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      label="Subcategory"
                      margin="normal"
                      placeholder={getSubcategories(formData.category).length ? 'Select or type a subcategory' : 'Select a category first'}
                      onChange={(e) => {
                        const v = e.target.value;
                        setFormData(prev => ({ ...prev, subcategory: v as any }));
                      }}
                    />
                  )}
                  disabled={!formData.category}
                />
                {/* Audience selection for Footwear */}
                <Autocomplete
                  options={getAudiencesForCategory(formData.category)}
                  value={(formData as any).audience || ''}
                  onChange={(_, newValue) => {
                    setFormData(prev => ({ ...prev, audience: (newValue || '') as any }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      label="Audience"
                      margin="normal"
                      placeholder={getAudiencesForCategory(formData.category).length ? 'Select audience' : 'Audience not applicable for this category'}
                      onChange={(e) => {
                        const v = e.target.value;
                        setFormData(prev => ({ ...prev, audience: v as any }));
                      }}
                    />
                  )}
                  disabled={getAudiencesForCategory(formData.category).length === 0}
                />
              </Box>
              <Box>
                <TextField
                  fullWidth
                  label="Price"
                  name="price"
                  type="number"
                  value={formData.price}
                  onChange={handleChange}
                  margin="normal"
                  required
                />
                <TextField
                  fullWidth
                  label="Original Price"
                  name="originalPrice"
                  type="number"
                  value={formData.originalPrice}
                  onChange={handleChange}
                  margin="normal"
                />
                <TextField
                  fullWidth
                  label="Image URL"
                  name="image"
                  value={formData.image}
                  onChange={handleChange}
                  margin="normal"
                  required
                />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    hidden
                    onChange={handleFilesSelected}
                  />
                  <Button variant="outlined" onClick={handleUploadClick} disabled={isUploading}>
                    {isUploading ? 'Uploading…' : 'Upload Images'}
                  </Button>
                  {uploadError && (
                    <Typography color="error" variant="caption">{uploadError}</Typography>
                  )}
                </Box>
                {/* Uploaded Images Gallery */}
                {uploadedImages.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Uploaded Images</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {uploadedImages.map((url) => (
                        <Box key={url} sx={{ position: 'relative', width: 90, height: 90, border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                          <Box component="img" src={url} alt="uploaded" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          <Box sx={{ position: 'absolute', top: 2, right: 2, display: 'flex', gap: 0.5 }}>
                            <Button size="small" variant="contained" color="primary" onClick={() => setFormData(prev => ({ ...prev, image: url }))} sx={{ minWidth: 0, px: 0.5 }}>Main</Button>
                            <IconButton size="small" color="error" onClick={() => handleRemoveImage(url)} aria-label="delete image" sx={{ bgcolor: 'rgba(255,255,255,0.85)' }}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      Click "Main" to set as primary image. Deleted images will not be saved.
                    </Typography>
                  </Box>
                )}
                <TextField
                  fullWidth
                  label="External URL (Product Link)"
                  name="url"
                  value={formData.url}
                  onChange={handleChange}
                  margin="normal"
                  type="url"
                  placeholder="https://example.com/product/123"
                  helperText="Optional. External page where users can view/buy this product."
                />
                <Box sx={{ mt: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.isOnSale}
                        onChange={handleChange}
                        name="isOnSale"
                      />
                    }
                    label="On Sale"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.isFeatured}
                        onChange={handleChange}
                        name="isFeatured"
                      />
                    }
                    label="Featured"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.isNew}
                        onChange={handleChange}
                        name="isNew"
                      />
                    }
                    label="New Arrival"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.isInStock}
                        onChange={handleChange}
                        name="isInStock"
                      />
                    }
                    label="In Stock"
                  />
                </Box>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button type="submit" variant="contained" color="primary">
              {editingProduct ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default AdminPanel;

// --- Helper component: User role manager ---
const UserRoleManager: React.FC = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const findUserByEmail = async (email: string) => {
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('email', '==', email));
    const snap = await getDocs(q);
    if (snap.empty) return null;
    // Assume first match
    const docSnap = snap.docs[0];
    return { id: docSnap.id, data: docSnap.data() as any };
  };

  const setRole = async (role: 'admin' | 'user') => {
    setMessage(null);
    setLoading(true);
    try {
      const trimmed = email.trim().toLowerCase();
      if (!trimmed) throw new Error('Please enter an email');
      const user = await findUserByEmail(trimmed);
      if (!user) throw new Error('No user found with that email');
      const ref = doc(db, 'users', user.id);
      await updateDoc(ref, { role, updatedAt: serverTimestamp() } as any);
      setMessage({ type: 'success', text: `Updated ${trimmed} to role: ${role}` });
    } catch (e: any) {
      const text = e?.message || 'Failed to update role';
      setMessage({ type: 'error', text });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {message && (
        <Alert severity={message.type} sx={{ mb: 2 }}>{message.text}</Alert>
      )}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
        <TextField
          label="User Email"
          placeholder="user@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          size="small"
          sx={{ minWidth: 280 }}
        />
        <Stack direction="row" spacing={1}>
          <Button variant="contained" disabled={loading || !email} onClick={() => setRole('admin')}>
            Make Admin
          </Button>
          <Button variant="outlined" disabled={loading || !email} onClick={() => setRole('user')}>
            Remove Admin
          </Button>
        </Stack>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
        This updates the user's role in Firestore (users/{'{' }uid{'}'}.role). Ensure your security rules restrict who can change roles.
      </Typography>
    </Box>
  );
};
